import { ROUTES } from '../../utils/constants';

export const DEALER_ALLOWED_ROUTES = [
  ROUTES.DEALER_WORKSPACE,
  ROUTES.HOME,
  ROUTES.HOME_LAUNCHER,
  ROUTES.LAUNCHER_SETTINGS,
  ROUTES.DASHBOARD,
  ROUTES.USER_MANAGEMENT,
  ROUTES.ADMIN_WORKSPACES,
  ROUTES.ADMIN_SHORTCUTS,
  ROUTES.ADMIN_TEMPLATES,
  ROUTES.ADMIN_FLOWS,
  ROUTES.ADMIN_ACTIONS,
  ROUTES.ADMIN_DEVICES,
  ROUTES.ADMIN_LOGS,
  ROUTES.CALL_ROUTING,
  ROUTES.INBOUND_RULES,
  ROUTES.SCHEDULED_TRIGGERS,
  ROUTES.SIMPLE_ACTIONS,
  ROUTES.AGENT_MANAGEMENT,
  ROUTES.ADMIN_ALGO_DEVICES,
  ROUTES.SUPPORT_REPORTS,
  ROUTES.TRAINING,
  ROUTES.ADMIN_SITES || '/admin/sites',
];

export const DEALER_EXCLUDED_ROUTES = [
  ROUTES.ADMIN_TENANTS,
  ROUTES.ADMIN_TENANT_DETAIL,
  ROUTES.ADMIN_BILLING,
  ROUTES.ADMIN_PRICING,
  ROUTES.ADMIN_DEALERS,
  ROUTES.ADMIN_DEALER_DETAIL,
  ROUTES.ADMIN_ALL_USERS,
  ROUTES.ADMIN_BACKUP_RECOVERY,
  ROUTES.ADMIN_SUPPORT,
  ROUTES.VOICE_SETTINGS,
  ROUTES.VOICE_DASHBOARD,
  ROUTES.TEST_TOOLS,
  ROUTES.REST_TEST_TOOLS,
  ROUTES.API_TEST_TOOLS,
  ROUTES.API_TEST_PAGE,
  ROUTES.ROLE_EDITOR,
  ROUTES.ADMIN_AGREEMENTS,
  ROUTES.CONFIG_SNAPSHOTS,
  ROUTES.ADMIN_CONTACT_LISTS,
];

export function isDealerWorkspacePath(pathname = '') {
  return pathname === '/dealer' || pathname.startsWith('/dealer/');
}

export function dealerWorkspaceIdsFromPath(pathname = '') {
  const match = String(pathname || '').match(/^\/dealer\/([^/]+)(?:\/tenants\/([^/]+))?\/?$/);
  if (!match) return { dealerId: '', tenantId: '' };
  return { dealerId: match[1] || '', tenantId: match[2] || '' };
}

export function shouldShowDealerTenantTools({ dealerShell = false, dealerActive = false, role } = {}) {
  if (!dealerShell) return true;
  if (dealerActive) return true;
  return role === 'admin' || role === 'superadmin' || role === 'tenant_admin';
}

export function isDealerShell({ pathname = '', dealerActive = false } = {}) {
  return Boolean(dealerActive) || isDealerWorkspacePath(pathname);
}

export function dealerLeaveDestination({ dealerId = '' } = {}) {
  if (dealerId) {
    return ROUTES.DEALER_WORKSPACE_DEALER.replace(':dealerId', dealerId);
  }
  return ROUTES.DEALER_WORKSPACE;
}

export function dealerAdminReturnPath({ dealerId = '', tenantId = '' } = {}) {
  if (dealerId && tenantId) {
    return ROUTES.DEALER_WORKSPACE_TENANT
      .replace(':dealerId', dealerId)
      .replace(':tenantId', tenantId);
  }
  if (dealerId) {
    return ROUTES.DEALER_WORKSPACE_DEALER.replace(':dealerId', dealerId);
  }
  return ROUTES.DEALER_WORKSPACE;
}

export function adminHomeDestination({
  role,
  dealerActive = false,
  dealerId = '',
  tenantId = '',
  hasDealerAccess = false,
} = {}) {
  if (dealerActive) return ROUTES.ADMIN_FLOWS;
  if (hasDealerAccess && role !== 'admin' && role !== 'superadmin' && role !== 'tenant_admin') {
    return dealerAdminReturnPath({ dealerId, tenantId });
  }
  return ROUTES.ADMIN_FLOWS;
}

export function isDealerExcludedPath(pathname = '') {
  return DEALER_EXCLUDED_ROUTES.some((route) => {
    const prefix = String(route || '').split(':')[0].replace(/\/$/, '');
    return pathname === prefix || pathname.startsWith(`${prefix}/`) || pathname.startsWith(prefix);
  });
}

export function canManageAssignedTenant({ dealerActive, isAdmin, isTenantAdmin } = {}) {
  return Boolean(dealerActive || isAdmin || isTenantAdmin);
}

export const DEALER_TENANT_NAV = [
  { id: 'dashboard', label: 'Dashboard', path: ROUTES.DASHBOARD, capability: 'tenant_overview' },
  { id: 'users', label: 'Users', path: ROUTES.USER_MANAGEMENT, capability: 'users' },
  { id: 'sites', label: 'Sites', path: '/admin/sites', capability: 'sites' },
  { id: 'homeDesigner', label: 'Home Designer', path: ROUTES.ADMIN_WORKSPACES, capability: 'home' },
  { id: 'shortcuts', label: 'Shortcuts', path: ROUTES.ADMIN_SHORTCUTS, capability: 'home' },
  { id: 'eventSystem', label: 'Event System', path: ROUTES.ADMIN_FLOWS, capability: 'flows' },
  { id: 'agents', label: 'Algo Agents', path: ROUTES.AGENT_MANAGEMENT, capability: 'agents' },
  { id: 'algoDevices', label: 'Algo devices', path: ROUTES.ADMIN_DEVICES, capability: 'agents' },
  { id: 'support', label: 'Support', path: ROUTES.SUPPORT_REPORTS, capability: 'support' },
];

export const DEALER_NAV_API_BY_ID = {
  dashboard: 'GET /api/v2/dealer/dealers/:dealerId/tenants/:tenantId',
  users: 'GET /api/users/list',
  sites: 'GET /api/v1/sites',
  homeDesigner: 'GET /api/v2/workspaces',
  shortcuts: 'GET /api/v2/users/me/prefs/shortcuts',
  eventSystem: 'GET /api/flows',
  agents: 'GET /api/v2/admin/agents/list',
  algoDevices: 'GET /api/devices',
  support: 'GET /api/supportReports',
};

export const DEALER_ALLOWED_PREFIXES = [
  '/admin/event-system',
  '/admin/workspaces',
  '/admin/shortcuts',
  '/admin/agent-management',
  '/admin/user-management',
  '/admin/algo-devices',
  '/admin/sites',
  '/admin/touch-panels',
  '/studio',
  '/dashboard',
  '/home',
  '/training',
  '/activity-feed',
  '/support',
];

export function dealerSidebarHomePath({
  dealerShell = false,
  dealerId = '',
  tenantId = '',
  pathname = '',
} = {}) {
  if (!dealerShell) return ROUTES.HOME_LAUNCHER;
  const fromPath = dealerWorkspaceIdsFromPath(pathname);
  return dealerAdminReturnPath({
    dealerId: dealerId || fromPath.dealerId,
    tenantId: tenantId || fromPath.tenantId,
  });
}

export function isDealerAllowedPath(pathname = '') {
  if (isDealerWorkspacePath(pathname)) return true;
  if (DEALER_ALLOWED_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return true;
  }
  return DEALER_ALLOWED_ROUTES.some((route) => {
    const prefix = String(route || '').split(':')[0].replace(/\/$/, '');
    return pathname === prefix || pathname.startsWith(`${prefix}/`);
  });
}
